import apollo from '@/graphql'

import AuditReviewQuery from '@/graphql/audit/AuditReviewQuery.gql'
import CompleteAudit from '@/graphql/audit/CompleteAudit.gql'
import MarkAuditIssueReviewed from '@/graphql/audit/MarkAuditIssueReviewed.gql'
import MarkIssuesUnresolvable from '@/graphql/audit/MarkIssuesUnresolvable.gql'
import RevertIssues from '@/graphql/audit/RevertIssues.gql'
import TakeLaneSidePicture from '@/graphql/lane_side/TakeLaneSidePicture.gql'
import ToggleAuditIssueStatus from '@/graphql/audit/ToggleAuditIssueStatus.gql'

// initial state
const state = {
  auditReview: {},
  auditReviewIssueIndex: null,
  auditReviewIssuesSaving: [],
}

// getters
const getters = {
  auditReview: st => st.auditReview,

  auditReviewCrop: (st, { auditReview, auditReviewIssue }) => {
    if (!auditReview.pogsRacks || !auditReview.rackCrops) return null
    const [pogRack] = auditReview.pogsRacks.filter(pr => pr.id === auditReviewIssue.pogsRacksId)
    const [rackCrop] = auditReview.rackCrops.filter(rc => rc.rack.id === pogRack.rack.id)
    return rackCrop
  },

  auditReviewImage: (st, { auditReview, auditReviewIssue }) =>
    (!auditReview.images ? null : auditReview.images
      .filter(image => image.laneSide.id === auditReviewIssue.pogRack.laneSide.id)[0] || {}),

  auditReviewIssue: (st, { auditReviewIssues }) =>
    (!auditReviewIssues ? null : auditReviewIssues[st.auditReviewIssueIndex]),

  auditReviewIssueIndex: st => st.auditReviewIssueIndex,

  auditReviewIssues: (st, {
    auditReview,
    auditReviewProductCoordMap,
    auditReviewProductMap,
    auditReviewRackMap,
  }) => (!auditReview.issues ? null : auditReview.issues
    .map((issue) => {
      const pogProduct = auditReviewProductMap[issue.pogsProductsId]
      const pogRack = auditReviewRackMap[issue.pogsRacksId]
      const productCoord = auditReviewProductCoordMap[issue.pogsProductsId][issue.pogsRacksId]
      return Object.assign({ pogProduct, pogRack, productCoord }, issue)
    })
    .filter(issue => issue.productCoord.isReviewable)),

  auditReviewIssuesSaving: st => st.auditReviewIssuesSaving,

  auditReviewPreviousImage: (st, { auditReview, auditReviewIssue }) =>
    (!auditReview.previousImages ? null : auditReview.previousImages
      .find(image => image.laneSide.id === auditReviewIssue.pogRack.laneSide.id) || {}),

  auditReviewProductCoordMap: (st, { auditReview }) => {
    const map = {}
    if (auditReview.productCoords) {
      auditReview.productCoords.forEach((coord) => {
        map[coord.pogsProductsId] = map[coord.pogsProductsId] || {}
        map[coord.pogsProductsId][coord.pogsRacksId] = coord
      })
    }
    return map
  },

  auditReviewProductMap: (st, { auditReview }) => {
    const map = {}
    if (auditReview.pogsProducts) {
      auditReview.pogsProducts.forEach((pogProduct) => {
        map[pogProduct.pogsProductsId] = pogProduct
      })
    }
    return map
  },

  auditReviewRackMap: (st, { auditReview }) => {
    const map = {}
    if (auditReview.pogsRacks) {
      auditReview.pogsRacks.forEach((pogRack) => {
        map[pogRack.id] = pogRack
      })
    }
    return map
  },

  auditReviewType: (st) => {
    switch (st.auditReview.status) {
      case 'Queued':
      case 'In Progress': return 'proofread'
      // case 'Awaiting Followup':
      // case 'Followup Started': return 'followup'
      default: return 'report'
    }
  },

  isAuditReviewImagePending: (st, { auditReview, auditReviewIssue }) =>
    (!auditReview.pendingImages ? null : auditReview.pendingImages
      .some(image => image.args.indexOf(`"lane_side":${auditReviewIssue.pogRack.laneSide.id}`) > -1)),

  isAuditReviewIssueWrongPlace: (st, { auditIssueWrongPlaceTypes, auditReviewIssue }) => {
    if (!auditIssueWrongPlaceTypes || !auditReviewIssue) return null
    return auditReviewIssue.type !== 'Not on Rack'
      && auditIssueWrongPlaceTypes.filter(type => type.name === auditReviewIssue.type).length
  },
}

// mutations
const mutations = {
  setAuditReview(st, auditReview) {
    state.auditReview = auditReview
    state.auditReviewIssuesSaving = []
  },

  setAuditReviewIssueIndex(st, index) {
    state.auditReviewIssueIndex = index
  },
}

// actions
const actions = {
  completeAudit(context, { id }) {
    return apollo.defaultClient.mutate({
      mutation: CompleteAudit,
      variables: { id: +id },
    })
  },

  async loadAuditReview({ commit }, { id, storesId, initialLoad }) {
    if (initialLoad) {
      Object.assign(state, {
        auditReview: {},
        auditReviewIssueIndex: null,
        auditReviewIssuesSaving: [],
      })
    }

    state.auditReviewIssueIndex = 0

    const { data: { auditReview } } = await apollo.defaultClient
      .query({
        fetchPolicy: 'network-only',
        query: AuditReviewQuery,
        variables: { id: +id, storesId: +storesId },
      })
    commit('setAuditReview', auditReview)
  },

  markAuditIssueReviewed({ getters: { auditReviewImage, auditReviewIssue } }) {
    return apollo.defaultClient.mutate({
      mutation: MarkAuditIssueReviewed,
      variables: {
        id: +(auditReviewIssue.id),
        imagesId: +(auditReviewImage.id),
      },
    })
  },

  markIssuesUnresolvable(context, { variables }) {
    return apollo.defaultClient.mutate({
      mutation: MarkIssuesUnresolvable,
      variables,
      refetchQueries: [{
        query: AuditReviewQuery,
        variables: { id: +(state.auditReview.id) },
      }],
    })
  },

  refreshAuditReviewImage({ getters: { auditReview, auditReviewIssue } }) {
    return apollo.defaultClient.mutate({
      mutation: TakeLaneSidePicture,
      variables: {
        auditsId: auditReview.id,
        laneSidesId: auditReviewIssue.pogRack.laneSide.id,
      },
      refetchQueries: [{
        query: AuditReviewQuery,
        variables: { id: +(state.auditReview.id) },
      }],
    })
  },

  revertIssues(context, { variables }) {
    return apollo.defaultClient.mutate({
      mutation: RevertIssues,
      variables,
      refetchQueries: [{
        query: AuditReviewQuery,
        variables: { id: +(state.auditReview.id) },
      }],
    })
  },

  toggleAuditIssueResolved(context, { variables }) {
    state.auditReviewIssuesSaving.push(variables.id)

    return apollo.defaultClient.mutate({
      mutation: ToggleAuditIssueStatus,
      variables,
      refetchQueries: [{
        query: AuditReviewQuery,
        variables: { id: +(state.auditReview.id) },
      }],
    })
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/audit/AuditReview.js